import type { SizeType, ToneType, VariantType } from './types';

export type ButtonVariantType = VariantType;
export type ButtonSizeType = SizeType;

export const buttonBaseClass =
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-transparent bg-clip-padding font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-busy:cursor-progress aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

export const buttonSizeStyles: Record<ButtonSizeType, string> = {
  small:
    'h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*=size-])]:size-3.5',
  medium:
    'h-8 gap-1.5 px-2.5 text-sm has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
  large: 'h-9 gap-1.5 px-3 text-sm has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
};

export const buttonToneStyles: Record<ButtonVariantType, Record<ToneType, string>> = {
  solid: {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700',
    destructive: 'bg-destructive text-white hover:bg-destructive/90',
    warning: 'bg-amber-600 text-white hover:bg-amber-700',
    accent: 'bg-fuchsia-600 text-white hover:bg-fuchsia-700',
    info: 'bg-sky-600 text-white hover:bg-sky-700',
    neutral: 'bg-zinc-700 text-white hover:bg-zinc-800',
  },
  subtle: {
    primary: 'bg-primary/10 text-primary hover:bg-primary/15',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    success: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
    warning: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
    accent: 'bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100',
    info: 'bg-sky-50 text-sky-700 hover:bg-sky-100',
    neutral: 'bg-muted text-foreground/80 hover:bg-muted/80',
  },
  outline: {
    primary: 'border-primary/40 bg-background text-primary hover:border-primary/60 hover:bg-background',
    secondary: 'border-border bg-background text-foreground hover:bg-muted',
    success: 'border-emerald-400 bg-background text-emerald-700 hover:bg-emerald-50',
    destructive: 'border-rose-400 bg-background text-rose-700 hover:bg-rose-50',
    warning: 'border-amber-400 bg-background text-amber-700 hover:bg-amber-50',
    accent: 'border-fuchsia-400 bg-background text-fuchsia-700 hover:bg-fuchsia-50',
    info: 'border-sky-400 bg-background text-sky-700 hover:bg-sky-50',
    neutral: 'border-border bg-background text-foreground hover:bg-muted',
  },
  ghost: {
    primary: 'bg-transparent text-primary hover:bg-primary/10',
    secondary: 'bg-transparent text-foreground hover:bg-muted',
    success: 'bg-transparent text-emerald-700 hover:bg-emerald-50',
    destructive: 'bg-transparent text-rose-700 hover:bg-rose-50',
    warning: 'bg-transparent text-amber-700 hover:bg-amber-50',
    accent: 'bg-transparent text-fuchsia-700 hover:bg-fuchsia-50',
    info: 'bg-transparent text-sky-700 hover:bg-sky-50',
    neutral: 'bg-transparent text-foreground/80 hover:bg-muted',
  },
  link: {
    primary: 'h-auto border-0 bg-transparent p-0 text-primary underline-offset-4 hover:underline',
    secondary:
      'h-auto border-0 bg-transparent p-0 text-secondary-foreground underline-offset-4 hover:underline',
    success:
      'h-auto border-0 bg-transparent p-0 text-emerald-700 underline-offset-4 hover:underline',
    destructive:
      'h-auto border-0 bg-transparent p-0 text-rose-700 underline-offset-4 hover:underline',
    warning:
      'h-auto border-0 bg-transparent p-0 text-amber-700 underline-offset-4 hover:underline',
    accent:
      'h-auto border-0 bg-transparent p-0 text-fuchsia-700 underline-offset-4 hover:underline',
    info: 'h-auto border-0 bg-transparent p-0 text-sky-700 underline-offset-4 hover:underline',
    neutral:
      'h-auto border-0 bg-transparent p-0 text-muted-foreground underline-offset-4 hover:underline',
  },
};
